// Lightweight loader for the Google Maps JS API.
// Returns { ready, error }. Safe to call from multiple components — the script
// is loaded exactly once even if the hook is mounted in several places.

import { useEffect, useState } from 'react';

const SCRIPT_ID = 'mq-google-maps-script';

let loadPromise = null;

function loadScript(apiKey) {
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Window not available'));
      return;
    }
    if (window.google?.maps) {
      resolve();
      return;
    }
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Maps script failed to load')));
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', () => reject(new Error('Maps script failed to load')));
    document.head.appendChild(script);
  });

  return loadPromise;
}

export default function useGoogleMaps(apiKey) {
  const [ready, setReady] = useState(!!window.google?.maps);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!apiKey) {
      setError(new Error('Missing REACT_APP_GOOGLE_MAPS_API_KEY'));
      return;
    }
    if (window.google?.maps) {
      setReady(true);
      return;
    }
    loadScript(apiKey)
      .then(() => setReady(true))
      .catch((err) => setError(err));
  }, [apiKey]);

  return { ready, error };
}
